/**
 * Nosto-ProductViewService.js
 *
 * Solicita vistas Liquid de producto renderizadas por Shopify.
 *
 * Se utiliza para obtener el HTML de los product cards y del modal
 * de compra rápida sin depender de GraphQL.
 *
 * Mantiene caché de respuestas, evita peticiones duplicadas y limita
 * la cantidad de solicitudes simultáneas realizadas al storefront.
 */

(function initializeNostoProductViewService(global) {
  "use strict";

  /*
   * Deduplicación reforzada, a nivel de módulo (no de instancia).
   *
   * El caché de getView() ya evita peticiones duplicadas dentro de
   * una misma instancia, pero cuando dos placements activos al mismo
   * tiempo (p.ej. dos tabs del carrusel del home) recomiendan el
   * mismo producto, ambos pueden alcanzar performRequest() antes de
   * que el otro haya registrado su promesa pendiente. Esta capa
   * comparte el in-flight por URL exacta entre TODAS las instancias
   * de NostoProductViewService de la página, así que ese caso
   * siempre reutiliza el mismo fetch en vez de duplicarlo.
   */
  const inFlightRequestsByUrl =
    new Map();

  class NostoProductViewService {
    /**
     * Crea el servicio encargado de solicitar vistas de producto.
     */
    constructor({
      cardView = "nosto-card",
      quickBuyView = "nosto-quick-buy",

      productPath = "/products",

      concurrency = 4,
      timeout = 15000,

      cache = true,
      cacheMaxAge = 5 * 60 * 1000,

      credentials = "same-origin",

      requestHeaders = {},

      validateCardSelector =
      "[data-nosto-product-card]",

      validateQuickBuySelector =
      "[data-nosto-quick-buy]",

      onRequestStart = null,
      onRequestEnd = null,
      onError = null,

      debug = false,
    } = {}) {
      this.cardView =
        this.normalizeViewName(
          cardView
        ) || "nosto-card";

      this.quickBuyView =
        this.normalizeViewName(
          quickBuyView
        ) || "nosto-quick-buy";

      this.productPath =
        this.normalizeProductPath(
          productPath
        );

      this.concurrency =
        this.normalizePositiveInteger(
          concurrency,
          4
        );

      this.timeout =
        this.normalizePositiveInteger(
          timeout,
          15000
        );

      this.cacheEnabled =
        Boolean(cache);

      this.cacheMaxAge =
        this.normalizeNonNegativeInteger(
          cacheMaxAge,
          5 * 60 * 1000
        );

      this.credentials =
        this.normalizeString(
          credentials
        ) || "same-origin";

      this.requestHeaders = {
        Accept: "text/html",
        "X-Requested-With":
          "XMLHttpRequest",

        ...requestHeaders,
      };

      this.validateCardSelector =
        this.normalizeString(
          validateCardSelector
        );

      this.validateQuickBuySelector =
        this.normalizeString(
          validateQuickBuySelector
        );

      this.onRequestStart =
        typeof onRequestStart ===
          "function"
          ? onRequestStart
          : null;

      this.onRequestEnd =
        typeof onRequestEnd ===
          "function"
          ? onRequestEnd
          : null;

      this.onError =
        typeof onError === "function"
          ? onError
          : null;

      this.debug =
        Boolean(debug);

      this.cache =
        new Map();

      this.pendingRequests =
        new Map();

      this.queue = [];

      this.activeRequestCount = 0;
    }

    /**
     * Escribe mensajes cuando el modo debug está habilitado.
     */
    log(...args) {
      if (!this.debug) return;

      console.info(
        "[NostoProductViewService]",
        ...args
      );
    }

    /**
     * Convierte cualquier valor en una cadena limpia.
     */
    normalizeString(value) {
      return String(value ?? "").trim();
    }

    /**
     * Normaliza un entero positivo.
     */
    normalizePositiveInteger(
      value,
      fallback
    ) {
      const number = Number(value);

      if (
        !Number.isFinite(number) ||
        number <= 0
      ) {
        return fallback;
      }

      return Math.floor(number);
    }

    /**
     * Normaliza un entero mayor o igual a cero.
     */
    normalizeNonNegativeInteger(
      value,
      fallback
    ) {
      const number = Number(value);

      if (
        !Number.isFinite(number) ||
        number < 0
      ) {
        return fallback;
      }

      return Math.floor(number);
    }

    /**
     * Normaliza el nombre de una vista Liquid.
     */
    normalizeViewName(view) {
      return this
        .normalizeString(view)
        .replace(/^\?*view=/i, "")
        .replace(
          /[^a-zA-Z0-9_-]/g,
          ""
        );
    }

    /**
     * Normaliza la ruta base de productos.
     */
    normalizeProductPath(path) {
      const normalizedPath =
        this.normalizeString(path) ||
        "/products";

      const withLeadingSlash =
        normalizedPath.startsWith("/")
          ? normalizedPath
          : `/${normalizedPath}`;

      return withLeadingSlash.replace(
        /\/+$/,
        ""
      );
    }

    /**
     * Normaliza un handle de Shopify.
     */
    normalizeHandle(handle) {
      return this
        .normalizeString(handle)
        .replace(/^\/+|\/+$/g, "")
        .split("?")[0]
        .split("#")[0];
    }

    /**
     * Normaliza un ID numérico o Global ID de Shopify.
     */
    normalizeProductId(productId) {
      const normalizedId =
        this.normalizeString(productId);

      if (!normalizedId) {
        return "";
      }

      return normalizedId
        .split("/")
        .filter(Boolean)
        .at(-1);
    }

    /**
     * Extrae el handle desde una URL de producto.
     */
    getHandleFromUrl(url) {
      const normalizedUrl =
        this.normalizeString(url);

      if (!normalizedUrl) {
        return "";
      }

      try {
        const parsedUrl =
          new URL(
            normalizedUrl,
            global.location.origin
          );

        const parts =
          parsedUrl.pathname
            .split("/")
            .filter(Boolean);

        const productsIndex =
          parts.indexOf(
            "products"
          );

        if (
          productsIndex >= 0 &&
          parts[productsIndex + 1]
        ) {
          return this.normalizeHandle(
            decodeURIComponent(
              parts[productsIndex + 1]
            )
          );
        }

        return this.normalizeHandle(
          decodeURIComponent(
            parts.at(-1) || ""
          )
        );
      } catch (error) {
        const productPathMatch =
          normalizedUrl.match(
            /\/products\/([^/?#]+)/
          );

        if (productPathMatch?.[1]) {
          return this.normalizeHandle(
            decodeURIComponent(
              productPathMatch[1]
            )
          );
        }

        return "";
      }
    }

    /**
     * Extrae el handle desde un hit de Nosto.
     */
    getHandleFromHit(hit = {}) {
      const directHandle =
        this.normalizeHandle(
          hit?.handle ||
          hit?.productHandle ||
          hit?.product_handle
        );

      if (directHandle) {
        return directHandle;
      }

      return this.getHandleFromUrl(
        hit?.url ||
        hit?.productUrl ||
        hit?.product_url
      );
    }

    /**
     * Obtiene un handle desde los datos disponibles en una solicitud.
     */
    resolveHandle({
      handle = "",
      hit = null,
      url = "",
    } = {}) {
      const normalizedHandle =
        this.normalizeHandle(
          handle
        );

      if (normalizedHandle) {
        return normalizedHandle;
      }

      const hitHandle =
        this.getHandleFromHit(
          hit || {}
        );

      if (hitHandle) {
        return hitHandle;
      }

      return this.getHandleFromUrl(
        url
      );
    }

    /**
     * Construye la URL de una vista Liquid de producto.
     */
    buildViewUrl({
      handle,
      view,
      parameters = {},
    }) {
      const normalizedHandle =
        this.normalizeHandle(
          handle
        );

      const normalizedView =
        this.normalizeViewName(
          view
        );

      if (!normalizedHandle) {
        throw new Error(
          "No es posible construir la URL sin un handle de producto."
        );
      }

      if (!normalizedView) {
        throw new Error(
          "No es posible construir la URL sin una vista válida."
        );
      }

      const url =
        new URL(
          `${this.productPath}/${encodeURIComponent(
            normalizedHandle
          )}`,
          global.location.origin
        );

      url.searchParams.set(
        "view",
        normalizedView
      );

      for (
        const [key, value] of
        Object.entries(
          parameters || {}
        )
      ) {
        if (
          value == null ||
          value === ""
        ) {
          continue;
        }

        url.searchParams.set(
          key,
          String(value)
        );
      }

      return (
        url.pathname +
        url.search
      );
    }

    /**
     * Construye una llave única para caché y peticiones pendientes.
     */
    createCacheKey({
      handle,
      view,
      parameters = {},
    }) {
      const normalizedParameters =
        Object.entries(
          parameters || {}
        )
          .filter(
            ([, value]) =>
              value != null &&
              value !== ""
          )
          .sort(
            ([keyA], [keyB]) =>
              keyA.localeCompare(
                keyB
              )
          )
          .map(
            ([key, value]) =>
              `${key}=${String(value)}`
          )
          .join("&");

      return [
        this.normalizeHandle(
          handle
        ),
        this.normalizeViewName(
          view
        ),
        normalizedParameters,
      ].join("::");
    }

    /**
     * Comprueba si una entrada de caché continúa vigente.
     */
    isCacheEntryValid(entry) {
      if (!entry) {
        return false;
      }

      if (
        this.cacheMaxAge === 0
      ) {
        return true;
      }

      return (
        Date.now() -
        entry.createdAt <
        this.cacheMaxAge
      );
    }

    /**
     * Obtiene HTML válido desde caché.
     */
    getCachedHtml(cacheKey) {
      if (!this.cacheEnabled) {
        return null;
      }

      const entry =
        this.cache.get(
          cacheKey
        );

      if (
        !this.isCacheEntryValid(
          entry
        )
      ) {
        this.cache.delete(
          cacheKey
        );

        return null;
      }

      return entry.html;
    }

    /**
     * Guarda HTML dentro de la caché.
     */
    setCachedHtml(
      cacheKey,
      html,
      metadata = {}
    ) {
      if (!this.cacheEnabled) {
        return;
      }

      this.cache.set(
        cacheKey,
        {
          html,
          metadata: {
            ...metadata,
          },
          createdAt:
            Date.now(),
        }
      );
    }

    /**
     * Agrega una tarea a la cola respetando el límite de concurrencia.
     */
    enqueue(task, {
      signal = null,
    } = {}) {
      return new Promise(
        (resolve, reject) => {
          if (signal?.aborted) {
            reject(
              this.createAbortError()
            );

            return;
          }

          const queueItem = {
            task,
            resolve,
            reject,
            signal,
            abortHandler: null,
          };

          if (signal) {
            queueItem.abortHandler =
              () => {
                const queueIndex =
                  this.queue.indexOf(
                    queueItem
                  );

                if (
                  queueIndex >= 0
                ) {
                  this.queue.splice(
                    queueIndex,
                    1
                  );

                  reject(
                    this.createAbortError()
                  );
                }
              };

            signal.addEventListener(
              "abort",
              queueItem.abortHandler,
              {
                once: true,
              }
            );
          }

          this.queue.push(
            queueItem
          );

          this.processQueue();
        }
      );
    }

    /**
     * Procesa tareas pendientes mientras exista capacidad disponible.
     */
    processQueue() {
      while (
        this.activeRequestCount <
        this.concurrency &&
        this.queue.length
      ) {
        const queueItem =
          this.queue.shift();

        if (!queueItem) {
          continue;
        }

        if (
          queueItem.signal?.aborted
        ) {
          queueItem.reject(
            this.createAbortError()
          );

          continue;
        }

        queueItem.signal?.removeEventListener(
          "abort",
          queueItem.abortHandler
        );

        this.activeRequestCount +=
          1;

        Promise.resolve()
          .then(queueItem.task)
          .then(
            queueItem.resolve,
            queueItem.reject
          )
          .finally(() => {
            this.activeRequestCount -=
              1;

            this.processQueue();
          });
      }
    }

    /**
     * Crea un error estándar para solicitudes canceladas.
     */
    createAbortError() {
      if (
        typeof DOMException ===
        "function"
      ) {
        return new DOMException(
          "La solicitud fue cancelada.",
          "AbortError"
        );
      }

      const error =
        new Error(
          "La solicitud fue cancelada."
        );

      error.name = "AbortError";

      return error;
    }

    /**
     * Combina una señal externa con el timeout interno.
     */
    createRequestController(
      externalSignal = null
    ) {
      const controller =
        new AbortController();

      let timeoutId = null;
      let externalAbortHandler =
        null;

      if (externalSignal) {
        if (
          externalSignal.aborted
        ) {
          controller.abort();
        } else {
          externalAbortHandler =
            () => {
              controller.abort();
            };

          externalSignal.addEventListener(
            "abort",
            externalAbortHandler,
            {
              once: true,
            }
          );
        }
      }

      if (this.timeout > 0) {
        timeoutId =
          global.setTimeout(
            () => {
              controller.abort();
            },
            this.timeout
          );
      }

      return {
        controller,

        /**
         * Elimina los listeners y timers asociados a la solicitud.
         */
        cleanup() {
          if (timeoutId) {
            global.clearTimeout(
              timeoutId
            );
          }

          if (
            externalSignal &&
            externalAbortHandler
          ) {
            externalSignal.removeEventListener(
              "abort",
              externalAbortHandler
            );
          }
        },
      };
    }

    /**
     * Obtiene el selector utilizado para validar una vista.
     */
    getValidationSelector(view) {
      const normalizedView =
        this.normalizeViewName(view);

      if (
        normalizedView ===
        "product-card"
      ) {
        return "[data-product-card]";
      }

      if (
        normalizedView ===
        "nosto-card" ||
        normalizedView ===
        "nosto-card-editorial" ||
        normalizedView ===
        "nosto-header-featured-carousel"
      ) {
        return "[data-nosto-product-card]";
      }

      if (
        normalizedView ===
        this.quickBuyView
      ) {
        return this
          .validateQuickBuySelector;
      }

      return "";
    }

    /**
     * Valida que el HTML recibido contenga la estructura esperada.
     */
    validateHtml(
      html,
      {
        view = "",
        selector = "",
      } = {}
    ) {
      const normalizedHtml =
        this.normalizeString(html);

      if (!normalizedHtml) {
        throw new Error(
          `La vista "${view}" devolvió una respuesta vacía.`
        );
      }

      const validationSelector =
        this.normalizeString(
          selector
        ) ||
        this.getValidationSelector(
          view
        );

      if (!validationSelector) {
        return normalizedHtml;
      }

      const template =
        document.createElement(
          "template"
        );

      template.innerHTML =
        normalizedHtml;

      if (
        !template.content.querySelector(
          validationSelector
        )
      ) {
        throw new Error(
          [
            `La vista "${view}" no contiene`,
            `"${validationSelector}".`,
          ].join(" ")
        );
      }

      return normalizedHtml;
    }

    /**
     * Ejecuta la petición HTTP de una vista de producto.
     */
    async performRequest(
      requestOptions
    ) {
      const {
        url,
        signal = null,
      } = requestOptions;

      /*
       * Solo deduplicamos peticiones sin signal propio: una petición
       * con AbortSignal puede cancelarse independientemente de las
       * demás, así que no es seguro compartirla.
       */
      if (
        !signal &&
        inFlightRequestsByUrl.has(
          url
        )
      ) {
        return inFlightRequestsByUrl.get(
          url
        );
      }

      const requestPromise =
        this.performRequestUncached(
          requestOptions
        );

      if (!signal) {
        inFlightRequestsByUrl.set(
          url,
          requestPromise
        );

        requestPromise.finally(
          () => {
            if (
              inFlightRequestsByUrl.get(
                url
              ) === requestPromise
            ) {
              inFlightRequestsByUrl.delete(
                url
              );
            }
          }
        );
      }

      return requestPromise;
    }

    async performRequestUncached({
      url,
      handle,
      view,
      productId = "",
      context = {},
      signal = null,
      validationSelector = "",
    }) {
      const requestControl =
        this.createRequestController(
          signal
        );

      const detail = {
        url,
        handle,
        view,
        productId:
          this.normalizeProductId(
            productId
          ),
        context: {
          ...context,
        },
      };

      this.onRequestStart?.(
        detail
      );

      this.log(
        "Solicitando vista.",
        detail
      );

      const startedAt =
        performance.now();

      try {
        const response =
          await fetch(url, {
            method: "GET",
            credentials:
              this.credentials,

            headers: {
              ...this.requestHeaders,
            },

            signal:
              requestControl
                .controller.signal,
          });

        if (!response.ok) {
          throw new Error(
            [
              `Shopify respondió ${response.status}`,
              `al solicitar "${url}".`,
            ].join(" ")
          );
        }

        const contentType =
          response.headers.get(
            "content-type"
          ) || "";

        if (
          contentType &&
          !contentType.includes(
            "text/html"
          )
        ) {
          throw new Error(
            [
              "La vista de producto no devolvió HTML.",
              `Content-Type: ${contentType}.`,
            ].join(" ")
          );
        }

        const html =
          this.validateHtml(
            await response.text(),
            {
              view,
              selector:
                validationSelector,
            }
          );

        const resultDetail = {
          ...detail,
          duration:
            performance.now() -
            startedAt,
          fromCache: false,
          html,
        };

        this.onRequestEnd?.(
          resultDetail
        );

        this.log(
          "Vista recibida.",
          {
            url,
            duration:
              resultDetail.duration,
          }
        );

        return html;
      } catch (error) {
        const normalizedError =
          this.normalizeRequestError(
            error,
            {
              url,
              view,
              signal,
            }
          );

        this.onError?.({
          ...detail,
          error:
            normalizedError,
        });

        throw normalizedError;
      } finally {
        requestControl.cleanup();
      }
    }

    /**
     * Normaliza errores de red, timeout y cancelación externa.
     */
    normalizeRequestError(
      error,
      {
        url,
        view,
        signal,
      }
    ) {
      if (
        error?.name ===
        "AbortError"
      ) {
        if (signal?.aborted) {
          return this.createAbortError();
        }

        const timeoutError =
          new Error(
            [
              `La vista "${view}" excedió`,
              `el tiempo máximo de ${this.timeout} ms.`,
              `URL: ${url}`,
            ].join(" ")
          );

        timeoutError.name =
          "TimeoutError";

        return timeoutError;
      }

      if (error instanceof Error) {
        return error;
      }

      return new Error(
        String(
          error ||
          "No fue posible solicitar la vista de producto."
        )
      );
    }

    /**
     * Solicita una vista utilizando caché, deduplicación y concurrencia.
     */
    async getView({
      handle = "",
      hit = null,
      url = "",
      productId = "",
      view = "",
      parameters = {},
      context = {},
      signal = null,
      forceRefresh = false,
      validationSelector = "",
    } = {}) {
      const resolvedHandle =
        this.resolveHandle({
          handle,
          hit,
          url,
        });

      const normalizedView =
        this.normalizeViewName(
          view
        );

      if (!resolvedHandle) {
        throw new Error(
          "No fue posible identificar el handle del producto."
        );
      }

      if (!normalizedView) {
        throw new Error(
          "No fue posible identificar la vista del producto."
        );
      }

      const cacheKey =
        this.createCacheKey({
          handle:
            resolvedHandle,
          view:
            normalizedView,
          parameters,
        });

      const canDeduplicate =
        !forceRefresh &&
        !signal;

      if (!forceRefresh) {
        const cachedHtml =
          this.getCachedHtml(
            cacheKey
          );

        if (cachedHtml) {
          this.log(
            "Vista obtenida desde caché.",
            {
              handle:
                resolvedHandle,
              view:
                normalizedView,
            }
          );

          this.onRequestEnd?.({
            handle:
              resolvedHandle,
            view:
              normalizedView,
            productId:
              this.normalizeProductId(
                productId
              ),
            context: {
              ...context,
            },
            fromCache: true,
            duration: 0,
            html:
              cachedHtml,
          });

          return cachedHtml;
        }
        if (canDeduplicate) {
          const pendingRequest =
            this.pendingRequests.get(
              cacheKey
            );

          if (pendingRequest) {
            this.log(
              "Reutilizando petición pendiente.",
              {
                handle:
                  resolvedHandle,
                view:
                  normalizedView,
              }
            );

            return pendingRequest;
          }
        }
      }

      const requestUrl =
        this.buildViewUrl({
          handle:
            resolvedHandle,
          view:
            normalizedView,
          parameters,
        });

      const requestPromise =
        this.enqueue(
          async () => {
            try {
              const html =
                await this.performRequest({
                  url:
                    requestUrl,

                  handle:
                    resolvedHandle,

                  view:
                    normalizedView,

                  productId,
                  context,
                  signal,

                  validationSelector,
                });

              this.setCachedHtml(
                cacheKey,
                html,
                {
                  handle:
                    resolvedHandle,
                  view:
                    normalizedView,
                  productId:
                    this.normalizeProductId(
                      productId
                    ),
                  url:
                    requestUrl,
                }
              );

              return html;
            } catch (error) {
              /*
               * Evita conservar una entrada anterior si el HTML
               * correspondiente al producto ya no es válido.
               */
              this.cache.delete(
                cacheKey
              );

              throw error;
            }
          },
          {
            signal,
          }
        );

      if (canDeduplicate) {
        this.pendingRequests.set(
          cacheKey,
          requestPromise
        );
      }

      try {
        return await requestPromise;
      } finally {
        if (
          canDeduplicate &&
          this.pendingRequests.get(
            cacheKey
          ) === requestPromise
        ) {
          this.pendingRequests.delete(
            cacheKey
          );
        }
      }
    }

    /**
     * Solicita la vista Liquid correspondiente a un product card.
     */
    getCard({
      handle = "",
      hit = null,
      url = "",
      productId = "",
      view = this.cardView,
      parameters = {},
      context = {},
      signal = null,
      forceRefresh = false,
    } = {}) {
      const normalizedView =
        this.normalizeViewName(view);

      return this.getView({
        handle,
        hit,
        url,
        productId,
        view: normalizedView,
        parameters,
        context,
        signal,
        forceRefresh,

        validationSelector:
          this.getValidationSelector(
            normalizedView
          ),
      });
    }

    /**
     * Solicita la vista Liquid correspondiente al quick buy.
     */
    getQuickBuy({
      handle = "",
      hit = null,
      url = "",
      productId = "",
      view = this.quickBuyView,
      parameters = {},
      context = {},
      signal = null,
      forceRefresh = false,
    } = {}) {
      return this.getView({
        handle,
        hit,
        url,
        productId,
        view,
        parameters,
        context,
        signal,
        forceRefresh,

        validationSelector:
          this.validateQuickBuySelector,
      });
    }

    /**
     * Precarga una vista sin insertarla en el DOM.
     */
    preload({
      type = "card",
      ...options
    } = {}) {
      return type ===
        "quick-buy"
        ? this.getQuickBuy(
          options
        )
        : this.getCard(
          options
        );
    }

    /**
     * Precarga varias vistas y devuelve el resultado individual de cada una.
     */
    preloadMany(
      items = [],
      {
        type = "card",
      } = {}
    ) {
      if (!Array.isArray(items)) {
        return Promise.resolve([]);
      }

      return Promise.allSettled(
        items.map((item) =>
          this.preload({
            ...item,
            type,
          })
        )
      );
    }

    /**
     * Elimina una vista específica de la caché.
     */
    invalidate({
      handle,
      view,
      parameters = {},
    }) {
      const normalizedHandle =
        this.normalizeHandle(
          handle
        );

      const normalizedView =
        this.normalizeViewName(
          view
        );

      if (
        !normalizedHandle ||
        !normalizedView
      ) {
        return false;
      }

      const cacheKey =
        this.createCacheKey({
          handle:
            normalizedHandle,
          view:
            normalizedView,
          parameters,
        });

      return this.cache.delete(
        cacheKey
      );
    }

    /**
     * Elimina de caché todas las vistas relacionadas con un producto.
     */
    invalidateProduct(handle) {
      const normalizedHandle =
        this.normalizeHandle(
          handle
        );

      if (!normalizedHandle) {
        return 0;
      }

      let removedCount = 0;

      for (
        const cacheKey of
        this.cache.keys()
      ) {
        if (
          cacheKey.startsWith(
            `${normalizedHandle}::`
          )
        ) {
          this.cache.delete(
            cacheKey
          );

          removedCount += 1;
        }
      }

      return removedCount;
    }

    /**
     * Vacía por completo la caché de vistas.
     */
    clearCache() {
      const previousSize =
        this.cache.size;

      this.cache.clear();

      return previousSize;
    }

    /**
     * Devuelve información útil para depuración del servicio.
     */
    getStats() {
      return {
        cacheSize:
          this.cache.size,

        pendingRequests:
          this.pendingRequests
            .size,

        queuedRequests:
          this.queue.length,

        activeRequests:
          this.activeRequestCount,

        concurrency:
          this.concurrency,

        cacheEnabled:
          this.cacheEnabled,

        cacheMaxAge:
          this.cacheMaxAge,
      };
    }

    /**
     * Cancela tareas que todavía no han comenzado y limpia la caché.
     */
    destroy() {
      const abortError =
        this.createAbortError();

      for (
        const queueItem of
        this.queue
      ) {
        queueItem.signal?.removeEventListener(
          "abort",
          queueItem.abortHandler
        );

        queueItem.reject(
          abortError
        );
      }

      this.queue = [];

      this.pendingRequests.clear();
      this.cache.clear();
    }
  }

  global.NostoProductViewService =
    NostoProductViewService;
})(window);